import { useNavigate } from "react-router";

import type { UserRoleDetailsDto } from "@/api/types";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { canManageRoles } from "../permissions";

interface RoleDetailsProps {
  roleDetails: UserRoleDetailsDto;
  onDelete: (id: string) => void;
}
export function RoleDetails(props: RoleDetailsProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const navigate = useNavigate();
  const {
    roleDetails: { id, name, type, locked, default: isRoleDefault },
  } = props;

  const handleDuplicateRole = () => {
    void navigate(
      routes.roles.create({
        slug,
      }),
      {
        state: {
          roleDetails: props.roleDetails,
        },
      },
    );
  };

  const menuItems: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      icon: <Edit05Icon />,
      href: routes.roles.edit({
        slug,
        id,
      }),
      isDisabled: locked,
      type: "link",
      value: "edit",
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      icon: <Copy01Icon />,
      callbackFn: handleDuplicateRole,
      value: "duplicate",
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      icon: <Trash01Icon />,
      callbackFn: () => props.onDelete(id),
      isDisabled: isRoleDefault,
      value: "delete",
    },
  ];

  return (
    <Stack gap="sm" data-testid="role-details">
      <Spread space="sm">
        <h2 className="text-heading-3">{t("page.role.section.general")}</h2>
        {hasPermission(canManageRoles) && <ContextMenu items={menuItems} />}
      </Spread>
      <dl>
        <Inline gap="xs" wrap={false}>
          <dt className="text-text-secondary">{t("model.role.name")}:</dt>
          <dd>{name}</dd>
        </Inline>
        <Inline gap="xs" wrap={false}>
          <dt className="text-text-secondary">{t("model.role.type")}:</dt>
          <dd>{type}</dd>
        </Inline>
      </dl>
    </Stack>
  );
}
